import React, { useState, useEffect } from 'react';
import '../css/schoolProfile.css';
import {
  FaSchool,
  FaIdCard,
  FaUser,
  FaMapMarkerAlt,
  FaEdit,
  FaSave,
  FaTimes,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useApp } from '../../../../context/AppContext';

// Mock school profile data
const mockSchoolProfile = {
  id: 1,
  semis_code: 'SCH001',
  school_name: 'Government Primary School, Karachi',
  emis_code: 'EMIS001',
  school_type: 'Mixed',
  level: 'Primary',
  taluka_id: 'T001',
  taluka_name: 'Karachi Central',
  headmaster_name: 'Muhammad Ali',
  phone: '021-1234567',
  email: 'gps.karachi@edu.pk',
  address: 'Block 6, PECHS, Karachi, Pakistan',
  location: '24.8607, 67.0011',
  created_at: '2024-01-15',
  updated_at: '2024-01-20 10:30 AM',
  status: 'Active',
};

function SchoolProfile() {
  const { user, roles, permissions } = useApp();
  const [profileData, setProfileData] = useState(mockSchoolProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditData(profileData);
  }, [profileData]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setEditData(profileData);
      setIsEditing(false);
    } else {
      // Start editing
      setIsEditing(true);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProfileData(editData);
      setIsEditing(false);

      Swal.fire({
        title: 'Success',
        text: 'School profile updated successfully!',
        icon: 'success',
        confirmButtonColor: '#4CAF50',
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to update school profile. Please try again.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = status => {
    const statusClass = status === 'Active' ? 'active' : 'inactive';
    return (
      <span className={`eduss-school-profile-status-badge ${statusClass}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="eduss-school-profile">
      <div className="eduss-school-profile-header">
        <div className="eduss-school-profile-title">
          <h2>School Profile</h2>
          <p>View and manage school information</p>
          <p>Roles: {roles && roles.length > 0 ? roles.join(', ') : 'None'}</p>
          <p>Permissions: {permissions && permissions.length > 0 ? permissions.join(', ') : 'None'}</p>
        </div>
        <div className="eduss-school-profile-actions">
          {/* Only show Edit if user has EDIT_SCHOOL permission */}
          {!isEditing && permissions.includes('EDIT_SCHOOL') && (
            <button onClick={handleEditToggle} className="eduss-school-profile-btn edit">
              <FaEdit /> Edit Profile
            </button>
          )}
          {isEditing && (
            <>
              <button onClick={handleEditToggle} className="eduss-school-profile-btn cancel" disabled={isSaving}>
                <FaTimes /> Cancel
              </button>
              <button onClick={handleSave} className="eduss-school-profile-btn save" disabled={isSaving}>
                <FaSave /> {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="eduss-school-profile-content">
        <div className="eduss-school-profile-sections">
          {/* Basic Information Section */}
          <div className="eduss-school-profile-section">
            <div className="eduss-school-profile-section-header">
              <FaSchool className="eduss-school-profile-section-icon" />
              <h3>Basic Information</h3>
            </div>

            <div className="eduss-school-profile-grid">
              <div className="eduss-school-profile-field">
                <label>School ID</label>
                <div className="eduss-school-profile-value">
                  {profileData.id}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>SEMIS Code</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="semis_code"
                      value={editData.semis_code}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                      maxLength={10}
                    />
                  ) : (
                    profileData.semis_code
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>School Name</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="school_name"
                      value={editData.school_name}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                    />
                  ) : (
                    profileData.school_name
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>EMIS Code</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="emis_code"
                      value={editData.emis_code}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                      maxLength={8}
                    />
                  ) : (
                    profileData.emis_code || 'Not provided'
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>School Type</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <select
                      name="school_type"
                      value={editData.school_type}
                      onChange={handleInputChange}
                      className="eduss-school-profile-select"
                    >
                      <option value="Boys">Boys</option>
                      <option value="Girls">Girls</option>
                      <option value="Mixed">Mixed</option>
                    </select>
                  ) : (
                    profileData.school_type
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Level</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <select
                      name="level"
                      value={editData.level}
                      onChange={handleInputChange}
                      className="eduss-school-profile-select"
                    >
                      <option value="Primary">Primary</option>
                      <option value="Middle">Middle</option>
                      <option value="High">High</option>
                      <option value="Higher Secondary">Higher Secondary</option>
                    </select>
                  ) : (
                    profileData.level
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Status</label>
                <div className="eduss-school-profile-value">
                  {getStatusBadge(profileData.status)}
                </div>
              </div>
            </div>
          </div>

          {/* Administrative Information Section */}
          <div className="eduss-school-profile-section">
            <div className="eduss-school-profile-section-header">
              <FaUser className="eduss-school-profile-section-icon" />
              <h3>Administrative Information</h3>
            </div>

            <div className="eduss-school-profile-grid">
              <div className="eduss-school-profile-field">
                <label>Taluka</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="taluka_name"
                      value={editData.taluka_name}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                    />
                  ) : (
                    profileData.taluka_name
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Headmaster Name</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="headmaster_name"
                      value={editData.headmaster_name}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                    />
                  ) : (
                    profileData.headmaster_name
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Phone Number</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={editData.phone}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                    />
                  ) : (
                    profileData.phone || 'Not provided'
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Email Address</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={editData.email}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                    />
                  ) : (
                    profileData.email || 'Not provided'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Location Information Section */}
          <div className="eduss-school-profile-section">
            <div className="eduss-school-profile-section-header">
              <FaMapMarkerAlt className="eduss-school-profile-section-icon" />
              <h3>Location Information</h3>
            </div>

            <div className="eduss-school-profile-grid">
              <div className="eduss-school-profile-field full-width">
                <label>Address</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <textarea
                      name="address"
                      value={editData.address}
                      onChange={handleInputChange}
                      className="eduss-school-profile-textarea"
                      rows={3}
                    />
                  ) : (
                    profileData.address || 'Not provided'
                  )}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Coordinates</label>
                <div className="eduss-school-profile-value">
                  {isEditing ? (
                    <input
                      type="text"
                      name="location"
                      value={editData.location}
                      onChange={handleInputChange}
                      className="eduss-school-profile-input"
                      placeholder="24.8607, 67.0011"
                    />
                  ) : (
                    profileData.location || 'Not provided'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* System Information Section */}
          <div className="eduss-school-profile-section">
            <div className="eduss-school-profile-section-header">
              <FaIdCard className="eduss-school-profile-section-icon" />
              <h3>System Information</h3>
            </div>

            <div className="eduss-school-profile-grid">
              <div className="eduss-school-profile-field">
                <label>Created Date</label>
                <div className="eduss-school-profile-value">
                  {profileData.created_at}
                </div>
              </div>

              <div className="eduss-school-profile-field">
                <label>Last Updated</label>
                <div className="eduss-school-profile-value">
                  {profileData.updated_at}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolProfile;
